import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView, trackProductPageView } from "@/lib/analytics";

import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { ProductGateway } from "@/components/site/ProductGateway";
import { CorporateStrengths } from "@/components/site/CorporateStrengths";
import { ProductPageHero } from "@/components/site/ProductPageHero";
import { AcrylicStrengths } from "@/components/site/AcrylicStrengths";
import { GalvalumPage } from "@/components/site/GalvalumPage";
import { Products } from "@/components/site/Products";
import { UVProtection } from "@/components/site/UVProtection";
import { Applications } from "@/components/site/Applications";
import { Gallery } from "@/components/site/Gallery";
import { Testimonials } from "@/components/site/Testimonials";
import { Partnership } from "@/components/site/Partnership";
import { CompanyProfile } from "@/components/site/CompanyProfile";
import { SalesTeam } from "@/components/site/SalesTeam";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";

const SITE_URL = "https://www.animaxputralancar.com";
const SITE_NAME = "Animax Putra Lancar";

const PAGE_SEO = {
  "/": {
    title: "PT. ANIMAX PUTRA LANCAR",
    description:
      "PT. Animax Putra Lancar menyediakan solusi material untuk kebutuhan proyek, kontraktor, retail, display, interior, dan industri.",
  },
  "/produk/animax-acrylic-sheet": {
    title: "ACRYLIC SHEET | PT. Animax Putra Lancar",
    description:
      "Acrylic Sheet dari PT. Animax Putra Lancar untuk kebutuhan signage, display, interior, dan proyek.",
    productName: "Acrylic",
  },
  "/produk/baja-ringan-galvalum": {
    title: "BAJA RINGAN & GALVALUM | PT. Animax Putra Lancar",
    description:
      "Baja Ringan dan Galvalum dari PT. Animax Putra Lancar untuk kebutuhan atap, roofing, struktur ringan, dan proyek.",
    productName: "Galvalum",
  },
  "/profil-perusahaan": {
    title: "Profil Perusahaan | PT. Animax Putra Lancar",
    description:
      "Profil PT. Animax Putra Lancar, perusahaan trading material untuk kebutuhan proyek, retail, display, interior, dan industri di Mojokerto.",
  },
  "/kontak": {
    title: "Kontak | PT. Animax Putra Lancar",
    description:
      "Hubungi PT. Animax Putra Lancar melalui WhatsApp, telepon, email, dan alamat kantor untuk kebutuhan material proyek dan industri.",
  },
};

const setMeta = (selector, attr, value) => {
  const tag = document.head.querySelector(selector);
  if (tag) tag.setAttribute(attr, value);
};

const PageMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = PAGE_SEO[pathname] || PAGE_SEO["/"];
    const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

    document.title = seo.title;
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[name="application-name"]', "content", SITE_NAME);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[property="og:title"]', "content", seo.title);
    setMeta('meta[property="og:description"]', "content", seo.description);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", seo.title);
    setMeta('meta[name="twitter:description"]', "content", seo.description);
  }, [pathname]);

  return null;
};

const ScrollToTarget = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const pagePath = `${pathname}${hash || ""}`;
    const seo = PAGE_SEO[pathname];

    trackPageView(pagePath);
    if (!hash && seo?.productName) {
      trackProductPageView(seo.productName, pathname);
    }

    if (hash) {
      window.setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 0);
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="home-page">
      <Navbar />
      <main>
        <Hero />
        <CompanyProfile />
        <ProductGateway />
        <CorporateStrengths />
        <Testimonials />
        <Partnership />
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const AnimaxAcrylicPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="animax-acrylic-page">
      <Navbar />
      <main>
        <ProductPageHero />
        <AcrylicStrengths />
        <Products />
        <UVProtection />
        <Applications />
        <Gallery />
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const BajaRinganGalvalumPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="baja-ringan-galvalum-page">
      <Navbar />
      <main>
        <GalvalumPage />
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const CompanyProfilePage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="company-profile-page">
      <Navbar />
      <main>
        <CompanyProfile />
        <CorporateStrengths />
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="contact-page">
      <Navbar />
      <main>
        <SalesTeam />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageMetadata />
        <ScrollToTarget />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/produk/animax-acrylic-sheet" element={<AnimaxAcrylicPage />} />
          <Route path="/produk/baja-ringan-galvalum" element={<BajaRinganGalvalumPage />} />
          <Route path="/profil-perusahaan" element={<CompanyProfilePage />} />
          <Route path="/kontak" element={<ContactPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
